import { serverRouteConfig } from "@server/server-route-config"
import { dashboardApiErrorMessage } from "@site-server/dashboard-api-errors"
import { createFileRoute } from "@tanstack/react-router"

/**
 * The return leg of the Google handshake.
 *
 * GET, because Supabase sends the BROWSER here -- and for the same reason
 * there is no origin guard. This is a cross-site top-level navigation whose
 * Origin is Supabase, not the dashboard, so requiring our own origin would
 * reject every real sign-in. What stands in its place is the SDK's own PKCE
 * verifier cookie, set when the handshake started and SameSite=Lax precisely
 * so it survives this hop; the code exchange fails without it.
 *
 * EVERY REPLY IS A REDIRECT, NEVER JSON. A person is looking at this URL --
 * they arrived by being sent here, not by fetching it -- so a JSON body is a
 * blank page with the answer hidden in view-source. Failures go to /login with
 * the message in the query string, where the form renders it.
 */
export const Route = createFileRoute("/api/dashboard/auth/callback")(
  serverRouteConfig({
    server: {
      handlers: {
        GET: async ({ request }) => {
          const url = new URL(request.url)
          try {
            const { completeDashboardSso } = await import("@api/features/dashboard-auth/dashboardSsoApi")
            const result = await completeDashboardSso({ url, request })

            /**
             * 303 rather than 302: it tells the browser to follow with GET
             * regardless of how it got here, and it must not be cached -- the
             * authorization code is single-use, so a cached redirect would
             * replay a code Supabase has already spent.
             */
            /** Session cookies are the SDK's; only the redirect is ours. */
            return new Response(null, {
              status: 303,
              headers: { Location: result.payload.returnTo, "Cache-Control": "no-store" },
            })
          } catch (error) {
            const message = dashboardApiErrorMessage(error, "Could not sign in.")
            return new Response(null, {
              status: 303,
              headers: {
                Location: `/login?error=${encodeURIComponent(message)}`,
                "Cache-Control": "no-store",
              },
            })
          }
        },
      },
    },
  })
)
